// Exporte: Stundenplan eines Kurses und Terminliste eines Dozenten als Druckansicht (PDF über den Druckdialog)
// sowie Termine eines Dozenten als Kalenderdatei (.ics). Alle Texte aus der Datenbank werden maskiert.
import type { Stand, Id, Einsatz } from './typen';
import { knotenPfad, fachbereichsKnoten } from './regeln';
import { deLang, deKurz, tagKurz, tagLang, kurzeZeit, kw, montag, plusTage } from './zeit';
import { moodleDaten } from '../moodle/daten';

const esc = (s: unknown) => String(s ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
const farbe = (f: string | null | undefined) => (f && /^#[0-9a-f]{3,8}$/i.test(f) ? f : '#073070');
const sichereUrl = (u: string | null) => (u && /^https?:\/\//i.test(u) ? esc(u) : '');

function erstelltAm(): string {
  const j = new Date(); const p = (n: number) => String(n).padStart(2, '0');
  return `${p(j.getDate())}.${p(j.getMonth() + 1)}.${j.getFullYear()} ${p(j.getHours())}:${p(j.getMinutes())}`;
}

/** Öffnet eine Druckansicht in einem neuen Fenster und startet den Druckdialog („Als PDF speichern“). */
function druckansicht(titel: string, primaer: string, inhalt: string): boolean {
  const w = window.open('', '_blank');
  if (!w) return false;
  w.document.write(`<!doctype html><html lang="de"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1">
<title>${esc(titel)}</title><style>
@page{size:A4;margin:14mm 12mm}
*{box-sizing:border-box}
body{font-family:Calibri,'Segoe UI',Arial,sans-serif;color:#242424;background:#fff;margin:24px;font-size:12px;line-height:1.4}
.leiste{display:flex;gap:8px;justify-content:flex-end;margin-bottom:16px}
.leiste button{font:inherit;font-weight:bold;padding:8px 14px;border-radius:8px;border:2px solid ${primaer};background:${primaer};color:#fff;cursor:pointer}
.leiste button.leise{background:#fff;color:${primaer}}
.kopf{display:flex;justify-content:space-between;gap:16px;align-items:flex-start;border-bottom:3px solid ${primaer};padding-bottom:10px;margin-bottom:14px}
.kicker{font-family:Arial,sans-serif;font-size:10px;font-weight:bold;letter-spacing:.12em;text-transform:uppercase;color:#5A5A5A}
h1{font-family:Arial,sans-serif;color:${primaer};font-size:20px;margin:2px 0 4px}
h2{font-family:Arial,sans-serif;color:${primaer};font-size:13px;margin:16px 0 6px}
p{margin:0}
.grau{color:#5A5A5A}
.rechts{text-align:right}
.logo{height:34px;width:auto;display:block;margin-left:auto;margin-bottom:4px}
table{border-collapse:collapse;width:100%}
table.fest{table-layout:fixed}
td{overflow-wrap:anywhere}
thead{display:table-header-group}
th{background:${primaer};color:#fff;text-align:left;padding:5px 6px;font-size:11px;-webkit-print-color-adjust:exact;print-color-adjust:exact}
td{border-bottom:1px solid #C9DCEF;padding:5px 6px;vertical-align:top}
tr{break-inside:avoid}
td.zahl,th.zahl{text-align:right;font-variant-numeric:tabular-nums;white-space:nowrap}
.nw{white-space:nowrap}
.entfaellt td{text-decoration:line-through;color:#5A5A5A}
.entfaellt td.hinweis{text-decoration:none}
.frei td{background:#F3F6FA;color:#5A5A5A;font-style:italic;-webkit-print-color-adjust:exact;print-color-adjust:exact}
.marke{display:inline-block;border:1px solid currentColor;border-radius:4px;padding:0 4px;font-size:10px;font-weight:bold}
.summe td{font-weight:bold;border-top:2px solid ${primaer};border-bottom:none}
.fuss{margin-top:18px;font-size:10px;color:#5A5A5A}
.leute{display:grid;grid-template-columns:repeat(auto-fill,minmax(220px,1fr));gap:4px 16px}
@media print{body{margin:0}.leiste{display:none}}
</style></head><body>
<div class="leiste"><button type="button" onclick="window.print()">Drucken oder als PDF speichern</button><button type="button" class="leise" onclick="window.close()">Schließen</button></div>
${inhalt}</body></html>`);
  w.document.close();
  w.focus();
  // Erst drucken, wenn das Logo geladen ist
  const bilder = [...w.document.images].map(i => (i.complete ? Promise.resolve() : new Promise<void>(r => { i.onload = i.onerror = () => r(); })));
  void Promise.all(bilder).then(() => w.print());
  return true;
}

function kopf(stand: Stand, kicker: string, titel: string, zeilen: string[]): string {
  const m = stand.mandant;
  return `<div class="kopf"><div><div class="kicker">${esc(kicker)}</div><h1>${esc(titel)}</h1>${zeilen.map(z => `<p class="grau">${z}</p>`).join('')}</div>
<div class="rechts">${sichereUrl(m.logo_url) ? `<img class="logo" src="${sichereUrl(m.logo_url)}" alt="">` : ''}<strong>${esc(m.name)}</strong>${m.einheit ? `<br><span class="grau">${esc(m.einheit)}</span>` : ''}</div></div>`;
}

/** Stundenplan eines Kurses (freigegebene und entfallene Termine, wie im Moodle-Stundenplan), nach Wochen gegliedert. */
export function kursStundenplanDrucken(stand: Stand, kursId: Id): boolean {
  const daten = moodleDaten(stand, kursId);
  if (!daten) return false;
  const k = daten.kurs;
  const zeiten = daten.zeitfenster.map(z => `${esc(z.bezeichnung)} ${esc(z.beginn)}–${esc(z.ende)} Uhr`).join(', ');

  // Termine und Feiertage nach Wochen gruppieren
  const wochen = new Map<string, string[]>();
  const zeile = (woche: string, html: string) => { const l = wochen.get(woche) ?? []; l.push(html); wochen.set(woche, l); };
  type Eintrag = { datum: string; sort: string; html: string };
  const eintraege: Eintrag[] = [
    ...daten.termine.map(t => ({
      datum: t.datum, sort: t.datum + t.beginn,
      html: `<tr${t.status === 'entfaellt' ? ' class="entfaellt"' : ''}><td class="nw">${tagKurz(t.datum)} ${deLang(t.datum)}</td><td class="nw">${esc(t.beginn)}–${esc(t.ende)}</td>
<td>${esc(t.fachbereich?.name ?? '')}${t.oberthema && t.oberthema !== t.fachbereich?.name ? `<br><span class="grau">${esc(t.oberthema)}</span>` : ''}</td><td>${esc(t.thema ?? '')}</td>
<td>${esc(t.dozent?.name ?? 'N. N.')}</td><td>${t.form === 'online' ? 'online' : esc(t.raum ?? '')}</td><td class="zahl">${t.ue}</td></tr>${t.status === 'entfaellt' || t.hinweis
        ? `<tr${t.status === 'entfaellt' ? ' class="entfaellt"' : ''}><td></td><td colspan="6" class="hinweis"><span class="marke">${t.status === 'entfaellt' ? 'entfällt' : 'Hinweis'}</span> ${esc(t.hinweis ?? '')}</td></tr>` : ''}`,
    })),
    ...daten.feiertage.filter(f => !daten.termine.some(t => t.datum === f.datum)).map(f => ({
      datum: f.datum, sort: f.datum + '00:00',
      html: `<tr class="frei"><td class="nw">${tagKurz(f.datum)} ${deLang(f.datum)}</td><td colspan="6">${esc(f.bezeichnung)} – kein Unterricht</td></tr>`,
    })),
  ].sort((a, b) => a.sort.localeCompare(b.sort));
  for (const e of eintraege) zeile(montag(e.datum), e.html);

  const tabellen = [...wochen.entries()].map(([mo, zeilen]) => `<h2>KW ${kw(mo)} · ${deKurz(mo)}–${deLang(plusTage(mo, 6))}</h2>
<table class="fest"><colgroup><col style="width:13%"><col style="width:11%"><col style="width:19%"><col style="width:24%"><col style="width:14%"><col style="width:13%"><col style="width:6%"></colgroup><thead><tr><th>Tag</th><th>Zeit</th><th>Fach</th><th>Thema</th><th>Dozent/in</th><th>Ort</th><th class="zahl">UE</th></tr></thead><tbody>${zeilen.join('')}</tbody></table>`).join('');

  const leute = new Map<string, { name: string; f: Set<string> }>();
  for (const t of daten.termine) if (t.dozent && t.status !== 'entfaellt') {
    const e = leute.get(t.dozent.kuerzel) ?? { name: t.dozent.name, f: new Set<string>() };
    if (t.fachbereich) e.f.add(t.fachbereich.name);
    leute.set(t.dozent.kuerzel, e);
  }
  const summe = daten.termine.filter(t => t.status !== 'entfaellt').reduce((s, t) => s + t.ue, 0);

  const inhalt = kopf(stand, `Stundenplan · ${k.kennung}`, k.bezeichnung, [
    `${deLang(k.beginn)} – ${deLang(k.ende)} · ${k.form === 'online' ? 'Online-Unterricht' : `Präsenz${k.standort ? ' in ' + esc(k.standort) : ''}`}`,
    ...(zeiten ? [zeiten] : []),
    `Planstand: ${esc(k.planstand)} · ${summe} UE`,
  ]) + (tabellen || '<p>Für diesen Kurs sind noch keine Termine freigegeben.</p>')
    + (leute.size ? `<h2>Dozierende und Fächer</h2><div class="leute">${[...leute.values()].sort((a, b) => a.name.split(' ').pop()!.localeCompare(b.name.split(' ').pop()!, 'de'))
      .map(x => `<div><strong>${esc(x.name)}</strong><br><span class="grau">${esc([...x.f].join('; '))}</span></div>`).join('')}</div>` : '')
    + `<p class="fuss">Enthalten sind alle freigegebenen Termine. Maßgeblich ist der jeweils aktuelle Planstand.</p>`;
  return druckansicht(`Stundenplan_${k.kennung}`, farbe(daten.mandant.farbe), inhalt);
}

/** Aufbereitete Angaben zu einem Einsatz (für Terminliste und Kalenderdatei) */
function einsatzInfo(stand: Stand, e: Einsatz) {
  const kurs = stand.kurse.find(k => k.id === e.kurs_id);
  const pfad = knotenPfad(stand, e.kurs_knoten_id).map(k => k.bezeichnung);
  const fb = stand.fachbereiche.find(f => f.id === fachbereichsKnoten(stand, e.kurs_knoten_id)?.fachbereich_id);
  const raum = stand.raeume.find(r => r.id === e.raum_id);
  const standort = stand.standorte.find(s => s.id === (raum?.standort_id ?? kurs?.standort_id));
  return { kurs, pfad, fb, raum, standort };
}

export interface TerminlisteFilter { von: string; bis: string; kursKennung: string | null; nurFreigegeben: boolean }

/** Terminliste eines Dozenten als Druckansicht. `liste` ist bereits gefiltert und sortiert; `bis` ist ausschließlich. */
export function dozentTerminlisteDrucken(stand: Stand, dozentId: Id, liste: Einsatz[], f: TerminlisteFilter): boolean {
  const d = stand.dozenten.find(x => x.id === dozentId);
  if (!d) return false;
  const summe = liste.reduce((s, e) => s + e.ue, 0);
  const zeilen = liste.map(e => {
    const i = einsatzInfo(stand, e);
    return `<tr><td class="nw">${tagKurz(e.datum)} ${deLang(e.datum)}</td><td class="nw">${kurzeZeit(e.beginn)}–${kurzeZeit(e.ende)}</td><td class="nw">${esc(i.kurs?.kennung ?? '')}</td>
<td>${esc(i.fb?.bezeichnung ?? '')}</td><td>${esc(i.pfad.join(' › '))}${e.hinweis ? `<br><span class="grau">${esc(e.hinweis)}</span>` : ''}</td>
<td>${e.form === 'online' ? 'online' : esc([i.raum?.bezeichnung, i.standort?.bezeichnung].filter(Boolean).join(', '))}</td>
<td class="zahl">${e.ue}</td><td>${e.status === 'entwurf' ? '<span class="marke">Entwurf</span>' : ''}</td></tr>`;
  }).join('');
  const name = [d.anrede, d.vorname, d.nachname].filter(Boolean).join(' ');
  const inhalt = kopf(stand, 'Einsätze und Stunden', `${name} (${d.kuerzel})`, [
    `Zeitraum ${deLang(f.von)} – ${deLang(plusTage(f.bis, -1))}${f.kursKennung ? ` · Kurs ${esc(f.kursKennung)}` : ' · alle Kurse'}${f.nurFreigegeben ? ' · nur freigegebene Termine' : ' · einschließlich Entwürfe'}`,
    `${liste.length} Einsätze · ${summe} UE · erstellt am ${erstelltAm()}`,
  ]) + (liste.length ? `<table><thead><tr><th>Datum</th><th>Zeit</th><th>Kurs</th><th>Fachbereich</th><th>Thema</th><th>Ort</th><th class="zahl">UE</th><th></th></tr></thead>
<tbody>${zeilen}<tr class="summe"><td colspan="6">Summe</td><td class="zahl">${summe}</td><td></td></tr></tbody></table>` : '<p>Keine Einsätze im Zeitraum.</p>');
  return druckansicht(`Einsaetze_${d.kuerzel}_${f.von}_bis_${plusTage(f.bis, -1)}`, farbe(stand.mandant.farbe_primaer), inhalt);
}

// ---------- Kalenderdatei (RFC 5545) ----------

const icsText = (s: string) => s.replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/,/g, '\\,').replace(/\r?\n/g, '\\n');

/** Zeilen nach 75 Bytes umbrechen (UTF-8-sicher) */
function falten(zeile: string): string {
  const enc = new TextEncoder();
  const teile: string[] = [];
  let akt = '', bytes = 0;
  for (const z of zeile) {
    const n = enc.encode(z).length;
    if (bytes + n > (teile.length ? 74 : 75)) { teile.push(akt); akt = ''; bytes = 0; }
    akt += z; bytes += n;
  }
  teile.push(akt);
  return teile.join('\r\n ');
}

const VTIMEZONE_BERLIN = [
  'BEGIN:VTIMEZONE', 'TZID:Europe/Berlin',
  'BEGIN:DAYLIGHT', 'TZOFFSETFROM:+0100', 'TZOFFSETTO:+0200', 'TZNAME:MESZ', 'DTSTART:19700329T020000', 'RRULE:FREQ=YEARLY;BYMONTH=3;BYDAY=-1SU', 'END:DAYLIGHT',
  'BEGIN:STANDARD', 'TZOFFSETFROM:+0200', 'TZOFFSETTO:+0100', 'TZNAME:MEZ', 'DTSTART:19701025T030000', 'RRULE:FREQ=YEARLY;BYMONTH=10;BYDAY=-1SU', 'END:STANDARD',
  'END:VTIMEZONE',
];

/** Erzeugt eine Kalenderdatei mit den übergebenen Einsätzen (Uhrzeiten in der Zeitzone des Mandanten). */
export function dozentKalender(stand: Stand, liste: Einsatz[]): string {
  const tz = stand.mandant.zeitzone || 'Europe/Berlin';
  const jetzt = new Date().toISOString().replace(/[-:]/g, '').replace(/\.\d+Z$/, 'Z');
  const zeit = (datum: string, u: string) => `${datum.replace(/-/g, '')}T${kurzeZeit(u).replace(':', '')}00`;
  const zeilen = ['BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//MeisterMind//Planung//DE', 'CALSCALE:GREGORIAN', 'METHOD:PUBLISH',
    `X-WR-CALNAME:${icsText(`Einsätze ${stand.mandant.name}`)}`, `X-WR-TIMEZONE:${tz}`, ...(tz === 'Europe/Berlin' ? VTIMEZONE_BERLIN : [])];
  for (const e of liste) {
    const i = einsatzInfo(stand, e);
    const titel = [...new Set([i.kurs?.kennung ?? 'Kurs', i.fb?.bezeichnung ?? 'Unterricht', i.pfad[i.pfad.length - 1]].filter(Boolean))].join(' · ');
    const ort = e.form === 'online' ? 'online' : [i.raum?.bezeichnung, i.standort?.bezeichnung, i.standort?.adresse].filter(Boolean).join(', ');
    const beschreibung = [
      `Kurs: ${i.kurs?.kennung ?? ''}${i.kurs ? ` – ${i.kurs.bezeichnung}` : ''}`,
      i.fb ? `Fachbereich: ${i.fb.bezeichnung}` : '',
      i.pfad.length ? `Thema: ${i.pfad.join(' › ')}` : '',
      `${e.ue} UE · ${e.form === 'online' ? 'online' : 'Präsenz'}`,
      e.status === 'entwurf' ? 'Status: Entwurf (noch nicht freigegeben)' : '',
      e.hinweis ? `Hinweis: ${e.hinweis}` : '',
      `${tagLang(e.datum)}, ${deLang(e.datum)}`,
    ].filter(Boolean).join('\n');
    zeilen.push('BEGIN:VEVENT', `UID:${e.id}@meistermind-planung`, `DTSTAMP:${jetzt}`,
      `DTSTART;TZID=${tz}:${zeit(e.datum, e.beginn)}`, `DTEND;TZID=${tz}:${zeit(e.datum, e.ende)}`,
      `SUMMARY:${icsText(titel)}`,
      ...(ort ? [`LOCATION:${icsText(ort)}`] : []),
      `DESCRIPTION:${icsText(beschreibung)}`,
      `STATUS:${e.status === 'entwurf' ? 'TENTATIVE' : 'CONFIRMED'}`, 'TRANSP:OPAQUE', 'END:VEVENT');
  }
  zeilen.push('END:VCALENDAR');
  return zeilen.map(falten).join('\r\n') + '\r\n';
}

/** Startet den Download einer Textdatei */
export function herunterladen(dateiname: string, inhalt: string, typ: string) {
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([inhalt], { type: typ }));
  a.download = dateiname;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
}
